// @flow
import React, {Fragment} from 'react';
import gql from 'graphql-tag';
import {Unveil} from '@homeaway/react-unveil';
import {debounce} from 'lodash';
import HitPlaceholder from './HitPlaceholder';
import HitCarouselContainer from '../HitCarouselContainer/HitCarouselContainer';
import HitInfo, {fragments as hitInfoFragments} from '../HitInfo/HitInfo';
import TripBoardButton from '../TripBoardButton/TripBoardButton';
import {PHOTOCAROUSEL_ADVANCE} from '../../utils/carouselEdapUtils';

import type {HitImage} from '../HitCarouselContainer/HitCarouselContainer';
import type {Shape as HitInfoShape} from '../HitInfo/HitInfo';

import './Hit.less';

export const fragments = {
    SERPHit: gql`
        fragment HitSERPHit on SERPHit {
            absolutePosition
            detailPageUrl
            imageCount
            images {
                lowResSrc
                src
                altText
            }
            popular
            relativePosition
            spu
            thumbnailUrl1
            ...HitInfoSERPHit
        }
        ${hitInfoFragments.SERPHit}
    `
};

export type Shape = {
    absolutePosition: number,
    detailPageUrl: string,
    imageCount: number,
    images: HitImage[],
    popular: boolean,
    relativePosition: number,
    spu: string,
    thumbnailUrl1: string
} & HitInfoShape;

export type Props = {
    hit?: Shape,
    isDatelessSearch?: boolean,
    isWithoutAnchor?: boolean,
    onHitClick?: (Shape) => void,
    onMouseEnter?: (listingId: string) => void,
    onMouseLeave?: (listingId: string) => void
};

type Target = '_blank' | '_self';

type CarouselProps = {
    hit?: Shape,
    onClick: (Shape) => ?void
};

export function Carousel({hit, onClick}: CarouselProps) {
    if (!hit) {
        return null;
    }

    return (
        <Fragment>
            <div className="Hit__tripboardButton">
                <TripBoardButton hit={hit} />
            </div>
            <HitCarouselContainer
                detailPageUrl={hit.detailPageUrl}
                edapAction={PHOTOCAROUSEL_ADVANCE}
                images={hit.images}
                listingId={hit.listingId}
                totalImageCount={hit.imageCount}
                onImageClick={onClick}
            />
        </Fragment>
    );
}

export function CarouselContainer({hit, onClick}: CarouselProps) {
    const carouselContentContainerClassname =
        'HitCarousel thumbnail--noMargin Hit__thumbnail content--clickable';

    if (typeof window !== 'undefined' && window.__IE11__) {
        return (
            <div className={carouselContentContainerClassname}>
                <Carousel hit={hit} onClick={onClick} />
            </div>
        );
    }

    return (
        <Unveil
            threshold={1200}
            throttleAmount={300}
            className={carouselContentContainerClassname}
        >
            <Carousel hit={hit} onClick={onClick} />
        </Unveil>
    );
}

export default function Hit(props: Props) {
    const {
        hit,
        isDatelessSearch,
        isWithoutAnchor,
        onHitClick,
        onMouseEnter,
        onMouseLeave
    } = props;
    const target: Target = '_blank';
    const handleMouseEnter = () =>
        hit && onMouseEnter ? onMouseEnter(hit.listingId) : null;
    const handleMouseLeave = () =>
        hit && onMouseLeave ? onMouseLeave(hit.listingId) : null;
    const onClick = () => (hit && onHitClick ? onHitClick(hit) : null);
    const onMouseTimeout = 100;

    return (
        <Fragment>
            {hit &&
                hit.spu &&
                !isWithoutAnchor && (
                    <span className="Hit__spu" id={`spu=${hit.spu}`} />
                )}
            <div
                className="Hit media-flex media-flex--left media-flex--xs"
                data-wdio="hit"
                onMouseEnter={debounce(handleMouseEnter, onMouseTimeout)}
                onMouseLeave={debounce(handleMouseLeave, onMouseTimeout)}
                role="button"
                tabIndex="0"
            >
                {!hit ? (
                    <HitPlaceholder />
                ) : (
                    <Fragment>
                        <CarouselContainer hit={hit} onClick={onClick} />
                        <div className="Hit__info">
                            {!isWithoutAnchor ? (
                                <a
                                    target={target}
                                    className="a--plain-link Hit__infoLink"
                                    href={hit.detailPageUrl}
                                    onClick={onClick}
                                >
                                    <HitInfo
                                        {...hit}
                                        isDatelessSearch={isDatelessSearch}
                                    />
                                </a>
                            ) : (
                                <HitInfo
                                    {...hit}
                                    isDatelessSearch={isDatelessSearch}
                                />
                            )}
                        </div>
                    </Fragment>
                )}
            </div>
        </Fragment>
    );
}
